import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Play, 
  CheckCircle, 
  XCircle,
  Loader2,
  Eye,
  EyeOff,
  Copy,
  Download,
  Key,
  Database,
  Calendar,
  MapPin,
  Filter,
  BarChart3,
  RefreshCw
} from 'lucide-react';

const AdminAPITest = () => {
  const [testResults, setTestResults] = useState({});
  const [loading, setLoading] = useState({});
  const [expandedResults, setExpandedResults] = useState({});
  const [showCredentials, setShowCredentials] = useState(false);
  const [testVillaId, setTestVillaId] = useState('8742');

  const credentials = {
    url: 'https://agents.villanovo.com/api',
    login: 'chris@priviacollection.com',
    password: 'ue6sfkQt'
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copié dans le presse-papier !');
  };

  const downloadJSON = (data, filename) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const runTest = async (testId, testFunction) => {
    setLoading(prev => ({ ...prev, [testId]: true }));
    try {
      const result = await testFunction();
      setTestResults(prev => ({
        ...prev,
        [testId]: {
          success: true,
          data: result,
          timestamp: new Date().toISOString()
        }
      }));
    } catch (error) {
      setTestResults(prev => ({
        ...prev,
        [testId]: {
          success: false,
          error: error.message,
          timestamp: new Date().toISOString()
        }
      }));
    } finally {
      setLoading(prev => ({ ...prev, [testId]: false }));
    }
  };

  const villanovoTests = [
    {
      id: 'villanovo-auth',
      title: 'Authentification',
      description: 'Teste la connexion à l\'API Villanovo',
      icon: Key,
      color: 'from-blue-500 to-blue-600',
      execute: async () => {
        const response = await fetch('/api/test/villanovo-auth', { method: 'POST' });
        if (!response.ok) throw new Error('Échec de l\'authentification');
        return await response.json();
      }
    },
    {
      id: 'villanovo-list',
      title: 'Liste des Villas',
      description: 'Récupère la liste complète (2295 villas)',
      icon: Database,
      color: 'from-green-500 to-green-600',
      execute: async () => {
        const response = await fetch('/api/test/villanovo-list', { method: 'POST' });
        if (!response.ok) throw new Error('Échec de récupération');
        return await response.json();
      }
    },
    {
      id: 'villanovo-villa',
      title: 'Détails d\'une Villa',
      description: `Récupère tous les détails (ID: ${testVillaId})`,
      icon: MapPin,
      color: 'from-purple-500 to-purple-600',
      execute: async () => {
        const response = await fetch(`/api/test/villanovo-villa/${testVillaId}`, { method: 'POST' });
        if (!response.ok) throw new Error('Échec de récupération');
        return await response.json();
      }
    },
    {
      id: 'villanovo-ical',
      title: 'Calendrier iCal',
      description: `Teste la récupération du calendrier (ID: ${testVillaId})`,
      icon: Calendar,
      color: 'from-amber-500 to-amber-600',
      execute: async () => {
        const response = await fetch(`/api/test/villanovo-ical/${testVillaId}`, { method: 'POST' });
        if (!response.ok) throw new Error('Échec de récupération');
        return await response.json();
      }
    },
    {
      id: 'villanovo-photos',
      title: 'Photos d\'une Villa',
      description: `Teste la structure photos (ID: ${testVillaId})`,
      icon: Database,
      color: 'from-pink-500 to-pink-600',
      execute: async () => {
        const response = await fetch(`/api/test/villanovo-photos/${testVillaId}`, { method: 'POST' });
        if (!response.ok) throw new Error('Échec de récupération');
        return await response.json();
      }
    },
    {
      id: 'villanovo-rates',
      title: 'Tarifs d\'une Villa',
      description: `Teste la structure tarifs (ID: ${testVillaId})`,
      icon: BarChart3,
      color: 'from-indigo-500 to-indigo-600',
      execute: async () => {
        const response = await fetch(`/api/test/villanovo-rates/${testVillaId}`, { method: 'POST' });
        if (!response.ok) throw new Error('Échec de récupération');
        return await response.json();
      }
    },
    {
      id: 'villanovo-equipments',
      title: 'Équipements d\'une Villa',
      description: `Teste la structure équipements (ID: ${testVillaId})`,
      icon: Filter,
      color: 'from-cyan-500 to-cyan-600',
      execute: async () => {
        const response = await fetch(`/api/test/villanovo-equipments/${testVillaId}`, { method: 'POST' });
        if (!response.ok) throw new Error('Échec de récupération');
        return await response.json();
      }
    },
    {
      id: 'villanovo-bedrooms',
      title: 'Chambres d\'une Villa',
      description: `Teste la structure chambres (ID: ${testVillaId})`,
      icon: MapPin,
      color: 'from-teal-500 to-teal-600',
      execute: async () => {
        const response = await fetch(`/api/test/villanovo-bedrooms/${testVillaId}`, { method: 'POST' });
        if (!response.ok) throw new Error('Échec de récupération');
        return await response.json();
      }
    }
  ];

  const projectTests = [
    {
      id: 'db-villas',
      title: 'Liste des Villas',
      description: 'Récupère les villas depuis MySQL (10 villas)',
      icon: Database,
      color: 'from-blue-500 to-blue-600',
      execute: async () => {
        const response = await fetch('/api/villas?limit=10');
        if (!response.ok) throw new Error('Échec de récupération');
        return await response.json();
      }
    },
    {
      id: 'db-villa-detail',
      title: 'Détails Villa',
      description: `Récupère les détails depuis MySQL (ID: ${testVillaId})`,
      icon: MapPin,
      color: 'from-green-500 to-green-600',
      execute: async () => {
        const response = await fetch(`/api/villas/${testVillaId}`);
        if (!response.ok) throw new Error('Échec de récupération');
        return await response.json();
      }
    },
    {
      id: 'db-availability',
      title: 'Disponibilités',
      description: `Récupère les disponibilités (ID: ${testVillaId})`,
      icon: Calendar,
      color: 'from-purple-500 to-purple-600',
      execute: async () => {
        const response = await fetch(`/api/villas/${testVillaId}/availability`);
        if (!response.ok) throw new Error('Échec de récupération');
        return await response.json();
      }
    },
    {
      id: 'filtering-rules',
      title: 'Règles de Filtrage',
      description: 'Récupère les règles d\'inclusion/exclusion',
      icon: Filter,
      color: 'from-amber-500 to-amber-600',
      execute: async () => {
        const response = await fetch('/api/filtering-rules');
        if (!response.ok) throw new Error('Échec de récupération');
        return await response.json();
      }
    },
    {
      id: 'destinations',
      title: 'Destinations',
      description: 'Récupère les pays et zones disponibles',
      icon: MapPin,
      color: 'from-pink-500 to-pink-600',
      execute: async () => {
        const response = await fetch('/api/destinations');
        if (!response.ok) throw new Error('Échec de récupération');
        return await response.json();
      }
    },
    {
      id: 'stats',
      title: 'Statistiques',
      description: 'Récupère les statistiques générales',
      icon: BarChart3,
      color: 'from-indigo-500 to-indigo-600',
      execute: async () => {
        const response = await fetch('/api/statistics');
        if (!response.ok) throw new Error('Échec de récupération');
        return await response.json();
      }
    },
    {
      id: 'sync-status',
      title: 'Statut Synchronisation',
      description: 'Récupère l\'état de la dernière sync',
      icon: RefreshCw,
      color: 'from-cyan-500 to-cyan-600',
      execute: async () => {
        const response = await fetch('/api/sync/status');
        if (!response.ok) throw new Error('Échec de récupération');
        return await response.json();
      }
    }
  ];

  const runAllTests = async () => {
    const allTests = [...villanovoTests, ...projectTests];
    for (const test of allTests) {
      await runTest(test.id, test.execute);
      // Petit délai entre chaque test pour éviter de surcharger l'API
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  };

  const runVillanovoTests = async () => {
    for (const test of villanovoTests) {
      await runTest(test.id, test.execute);
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  };

  const runProjectTests = async () => {
    for (const test of projectTests) {
      await runTest(test.id, test.execute);
      await new Promise(resolve => setTimeout(resolve, 300));
    }
  };

  const toggleExpanded = (testId) => {
    setExpandedResults(prev => ({
      ...prev,
      [testId]: !prev[testId]
    }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Tests API</h2>
          <p className="text-gray-600 mt-2">
            Testez tous les endpoints de l'API Villanovo et de la base de données
          </p>
        </div>
        <Button
          onClick={runAllTests}
          disabled={Object.values(loading).some(l => l)}
          className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800"
        >
          <Play className="w-4 h-4 mr-2" />
          Tout Tester
        </Button>
      </div>

      {/* Credentials Card */}
      <Card className="p-6 bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Key className="w-5 h-5 text-slate-600" />
            <h3 className="font-semibold text-gray-900">Credentials API Villanovo</h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowCredentials(!showCredentials)}
          >
            {showCredentials ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </Button>
        </div>
        
        {showCredentials && (
          <div className="space-y-2 text-sm font-mono bg-white p-4 rounded-lg border">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">URL:</span>
              <div className="flex items-center gap-2">
                <span className="text-gray-900">{credentials.url}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(credentials.url)}
                >
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Login:</span>
              <div className="flex items-center gap-2">
                <span className="text-gray-900">{credentials.login}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(credentials.login)}
                >
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Password:</span>
              <div className="flex items-center gap-2">
                <span className="text-gray-900">{credentials.password}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(credentials.password)}
                >
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Villa ID Input */}
      <Card className="p-6 bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <MapPin className="w-5 h-5 text-amber-600" />
            <h3 className="font-semibold text-gray-900">Villa ID pour les Tests</h3>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label htmlFor="villaId" className="block text-sm font-medium text-gray-700 mb-2">
              ID de la villa à tester
            </label>
            <input
              id="villaId"
              type="text"
              value={testVillaId}
              onChange={(e) => setTestVillaId(e.target.value)}
              className="w-full px-4 py-2 border border-amber-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white text-gray-900 font-mono"
              placeholder="8742"
            />
          </div>
          <div className="flex flex-col items-center justify-center pt-6">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setTestVillaId('8742')}
              className="border-amber-600 text-amber-700 hover:bg-amber-50"
            >
              Réinitialiser
            </Button>
          </div>
        </div>
        
        <p className="text-sm text-gray-600 mt-3 flex items-start gap-2">
          <span className="text-amber-600 font-bold">ℹ️</span>
          Cet ID sera utilisé pour tous les tests qui nécessitent un identifiant de villa spécifique (détails, photos, tarifs, etc.)
        </p>
      </Card>

      {/* Section API Villanovo */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900">🌐 API Villanovo</h3>
            <p className="text-sm text-gray-600">Tests de l'API externe Villanovo</p>
          </div>
          <Button
            onClick={runVillanovoTests}
            disabled={Object.values(loading).some(l => l)}
            variant="outline"
            className="border-blue-600 text-blue-700 hover:bg-blue-50"
          >
            <Play className="w-4 h-4 mr-2" />
            Tester Villanovo
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {villanovoTests.map((test) => {
            const Icon = test.icon;
            const result = testResults[test.id];
            const isLoading = loading[test.id];
            const isExpanded = expandedResults[test.id];

            return (
              <Card key={test.id} className="p-4">
                {/* Header */}
                <div className="flex items-start gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${test.color} flex items-center justify-center flex-shrink-0`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 text-sm mb-1">{test.title}</h3>
                    <p className="text-xs text-gray-600">{test.description}</p>
                  </div>
                </div>

                {/* Status Badge */}
                {result && (
                  <div className="mb-3">
                    <Badge
                      variant={result.success ? 'default' : 'destructive'}
                      className={`text-xs ${
                        result.success 
                          ? 'bg-green-100 text-green-800 border-green-200' 
                          : 'bg-red-100 text-red-800 border-red-200'
                      }`}
                    >
                      {result.success ? (
                        <>
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Réussi
                        </>
                      ) : (
                        <>
                          <XCircle className="w-3 h-3 mr-1" />
                          Échec
                        </>
                      )}
                    </Badge>
                    <span className="text-xs text-gray-500 ml-2">
                      {new Date(result.timestamp).toLocaleTimeString('fr-FR')}
                    </span>
                  </div>
                )}

                {/* Action Button */}
                <Button
                  onClick={() => runTest(test.id, test.execute)}
                  disabled={isLoading}
                  className="w-full mb-2"
                  variant="outline"
                  size="sm"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Test en cours...
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4 mr-2" />
                      Lancer le test
                    </>
                  )}
                </Button>

                {/* Result Preview */}
                {result && (
                  <div className="space-y-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleExpanded(test.id)}
                      className="w-full text-xs"
                    >
                      {isExpanded ? 'Masquer' : 'Voir'} les détails
                    </Button>

                    {isExpanded && (
                      <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                        {result.success ? (
                          <>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-semibold text-gray-700">Résultat:</span>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => downloadJSON(result.data, `${test.id}-result.json`)}
                              >
                                <Download className="w-3 h-3" />
                              </Button>
                            </div>
                            <pre className="text-xs bg-white p-2 rounded border border-slate-300 overflow-auto max-h-48">
                              {JSON.stringify(result.data, null, 2)}
                            </pre>
                          </>
                        ) : (
                          <div className="text-xs text-red-600">
                            <strong>Erreur:</strong> {result.error}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </div>

      {/* Section API Projet */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900">🏠 API Projet (MySQL)</h3>
            <p className="text-sm text-gray-600">Tests de l'API interne du projet</p>
          </div>
          <Button
            onClick={runProjectTests}
            disabled={Object.values(loading).some(l => l)}
            variant="outline"
            className="border-green-600 text-green-700 hover:bg-green-50"
          >
            <Play className="w-4 h-4 mr-2" />
            Tester Projet
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {projectTests.map((test) => {
          const Icon = test.icon;
          const result = testResults[test.id];
          const isLoading = loading[test.id];
          const isExpanded = expandedResults[test.id];

          return (
            <Card key={test.id} className="p-4">
              {/* Header */}
              <div className="flex items-start gap-3 mb-3">
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${test.color} flex items-center justify-center flex-shrink-0`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 text-sm mb-1">{test.title}</h3>
                  <p className="text-xs text-gray-600">{test.description}</p>
                </div>
              </div>

              {/* Status Badge */}
              {result && (
                <div className="mb-3">
                  <Badge
                    variant={result.success ? 'default' : 'destructive'}
                    className={`text-xs ${
                      result.success 
                        ? 'bg-green-100 text-green-800 border-green-200' 
                        : 'bg-red-100 text-red-800 border-red-200'
                    }`}
                  >
                    {result.success ? (
                      <>
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Réussi
                      </>
                    ) : (
                      <>
                        <XCircle className="w-3 h-3 mr-1" />
                        Échec
                      </>
                    )}
                  </Badge>
                  <span className="text-xs text-gray-500 ml-2">
                    {new Date(result.timestamp).toLocaleTimeString('fr-FR')}
                  </span>
                </div>
              )}

              {/* Action Button */}
              <Button
                onClick={() => runTest(test.id, test.execute)}
                disabled={isLoading}
                className="w-full mb-2"
                variant="outline"
                size="sm"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Test en cours...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Lancer le test
                  </>
                )}
              </Button>

              {/* Result Preview */}
              {result && (
                <div className="space-y-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleExpanded(test.id)}
                    className="w-full text-xs"
                  >
                    {isExpanded ? 'Masquer' : 'Voir'} les détails
                  </Button>

                  {isExpanded && (
                    <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                      {result.success ? (
                        <>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold text-gray-700">Résultat:</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => downloadJSON(result.data, `${test.id}-result.json`)}
                            >
                              <Download className="w-3 h-3" />
                            </Button>
                          </div>
                          <pre className="text-xs bg-white p-2 rounded border border-slate-300 overflow-auto max-h-48">
                            {JSON.stringify(result.data, null, 2)}
                          </pre>
                        </>
                      ) : (
                        <div className="text-xs text-red-600">
                          <strong>Erreur:</strong> {result.error}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      </div>

      {/* Summary */}
      {Object.keys(testResults).length > 0 && (
        <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <h3 className="font-semibold text-gray-900 mb-4">Résumé des Tests</h3>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-gray-900">{Object.keys(testResults).length}</p>
              <p className="text-sm text-gray-600">Tests exécutés</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">
                {Object.values(testResults).filter(r => r.success).length}
              </p>
              <p className="text-sm text-gray-600">Réussis</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">
                {Object.values(testResults).filter(r => !r.success).length}
              </p>
              <p className="text-sm text-gray-600">Échecs</p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default AdminAPITest;

